import React, { useState } from 'react';
import { Modal, Spin, Typography, Alert } from 'antd';
import {
    MailOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import style from './style.module.scss';
import { Tag } from '../../../../../components/tag';

const { Text } = Typography;

interface EmailValidationModalProps {
    isVisible: boolean;
    clientId: number | null | undefined;
    clientEmail: string | null | undefined;
    isEmailConfirmed?: boolean;
    isValidating: boolean;
    onValidate: (id: number) => Promise<void>;
    onClose: () => void;
}

const EmailValidationModal: React.FC<EmailValidationModalProps> = ({
    isVisible,
    clientId,
    clientEmail,
    isEmailConfirmed = false,
    isValidating,
    onValidate,
    onClose,
}) => {
    const [validationSuccess, setValidationSuccess] = useState(false);
    const [localEmailConfirmed, setLocalEmailConfirmed] = useState(
        isEmailConfirmed
    );

    React.useEffect(() => {
        if (isVisible) {
            setValidationSuccess(false);
            setLocalEmailConfirmed(isEmailConfirmed);
        }
    }, [isVisible, isEmailConfirmed]);

    const handleValidate = async () => {
        if (clientId) {
            try {
                await onValidate(clientId);
                setValidationSuccess(true);
                setLocalEmailConfirmed(true);
            } catch (error) {
                console.error('Email validation failed:', error);
            }
        }
    };

    return (
        <Modal
            title={
                <div className={style.modalHeader}>
                    <MailOutlined className={style.emailIcon} />
                    <Text strong>Email Validation</Text>
                </div>
            }
            open={isVisible}
            onOk={handleValidate}
            onCancel={onClose}
            confirmLoading={isValidating}
            okText="Validate Email"
            cancelText="Cancel"
            okButtonProps={{
                disabled: validationSuccess || localEmailConfirmed,
            }}
            centered
        >
            <div className={style.statusSection}>
                <Text strong>Current Status: </Text>
                {localEmailConfirmed ? (
                    <Tag
                        variant="success"
                        icon={<CheckCircleOutlined />}
                        className={style.statusTag}
                    >
                        Verified
                    </Tag>
                ) : (
                    <Tag
                        variant="orange"
                        icon={<ClockCircleOutlined />}
                        className={style.statusTag}
                    >
                        Not Verified
                    </Tag>
                )}
            </div>

            {isValidating ? (
                <div className={style.loadingContainer}>
                    <Spin size="large" />
                </div>
            ) : validationSuccess ? (
                <Alert
                    message="Email Validated Successfully"
                    description={`Email address ${clientEmail} has been successfully validated.`}
                    type="success"
                    showIcon
                    className={style.successAlert}
                />
            ) : (
                <div className={style.contentContainer}>
                    {localEmailConfirmed ? (
                        <p>
                            The email address <strong>{clientEmail}</strong> is
                            already verified.
                        </p>
                    ) : (
                        <>
                            <p>
                                Are you sure you want to directly validate the
                                email address <strong>{clientEmail}</strong>?
                            </p>
                            <p>
                                This will mark the email as confirmed without
                                sending a verification email to the user.
                            </p>
                        </>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default EmailValidationModal;
